import { useEffect, useRef } from 'react';

type Vec = [number, number, number];
type Face = [number, number, number];
type Bounds = { left: number; top: number; right: number; bottom: number };

// Cull only geometry completely outside the visible crop. Rear faces stay:
// this crystal is translucent, so back-face culling would change its appearance.
const overlaps = (points: { x: number; y: number }[], bounds: Bounds, margin = 16) =>
  !points.every(p => p.x < bounds.left - margin)
  && !points.every(p => p.x > bounds.right + margin)
  && !points.every(p => p.y < bounds.top - margin)
  && !points.every(p => p.y > bounds.bottom + margin);

// A closed circle in the crystal's local coordinates, not a screen-space ellipse.
const orbitPoint = (angle: number, radius: number): Vec => [
  Math.cos(angle) * radius,
  Math.sin(angle) * radius * Math.sin(0.42),
  Math.sin(angle) * radius * Math.cos(0.42),
];
const orbits = [1.32, 1.46].map(radius =>
  Array.from({ length: 128 }, (_, i) => orbitPoint(i / 128 * Math.PI * 2, radius)),
);
const orbitTicks = Array.from({ length: 48 }, (_, i) => {
  const angle = i / 48 * Math.PI * 2;
  return {
    start: orbitPoint(angle, 1.46),
    end: orbitPoint(angle, 1.46 + (i % 4 === 0 ? 0.028 : 0.013)),
    major: i % 4 === 0,
  };
});

// Exact critically damped spring: continuous velocity, no overshoot, and the
// same response at different refresh rates. Input changes never snap the pose.
const followPointer = (position: number, velocity: number, target: number, dt: number) => {
  const frequency = 7.5;
  const offset = position - target;
  const impulse = (velocity + frequency * offset) * dt;
  const decay = Math.exp(-frequency * dt);
  return [target + (offset + impulse) * decay, (velocity - frequency * impulse) * decay];
};

// An abstract, faceted study of a learned layer. Not experimental data.
// Canvas 2D projection keeps the scene light: no WebGL or animation dependency.
const vertices: Vec[] = [[0.08, -1.35, 0.02]];
for (let i = 0; i < 6; i++) {
  const a = (i / 6) * Math.PI * 2;
  vertices.push([Math.cos(a) * 0.73, -0.42 + (i % 2) * 0.055, Math.sin(a) * 0.73]);
}
for (let i = 0; i < 6; i++) {
  const a = (i / 6) * Math.PI * 2 + 0.17;
  vertices.push([Math.cos(a) * 0.81, 0.46 - (i % 2) * 0.04, Math.sin(a) * 0.81]);
}
vertices.push([-0.06, 1.32, -0.04]);
const faces: Face[] = [];
for (let i = 0; i < 6; i++) {
  const n = (i + 1) % 6;
  faces.push([0, 1 + i, 1 + n], [1 + i, 7 + i, 1 + n], [1 + n, 7 + i, 7 + n], [13, 7 + n, 7 + i]);
}

export function FacetedScene() {
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const stage = stageRef.current;
    if (!canvas || !stage) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pointerSurface = canvas.closest<HTMLElement>('.hero') ?? canvas;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    let reduced = motion.matches;
    let visible = true;
    let frame = 0;
    let width = 600;
    let height = 640;
    let rasterBounds: Bounds = { left: 0, top: 0, right: 600, bottom: 640 };
    let visibleBounds = rasterBounds;
    let hasArea = false;
    let time = 0;
    let previous = 0;
    let pointerX = 0;
    let pointerY = 0;
    let easedX = 0;
    let easedY = 0;
    let velocityX = 0;
    let velocityY = 0;

    const draw = () => {
      if (!hasArea) return;
      ctx.clearRect(rasterBounds.left, rasterBounds.top, rasterBounds.right - rasterBounds.left, rasterBounds.bottom - rasterBounds.top);
      const drawableBounds = reduced ? rasterBounds : visibleBounds;
      // Keep the original site's oversized, partially concealed hero composition.
      const size = Math.min(width, height) * 0.46;
      const centerX = width * 0.60;
      const centerY = height * 0.69;
      const angle = 0.52 + time * 0.045 + easedX * 0.12;
      const tilt = -0.16 + easedY * 0.055;
      const cosYaw = Math.cos(angle), sinYaw = Math.sin(angle);
      const cosTilt = Math.cos(tilt), sinTilt = Math.sin(tilt);
      const cosRoll = Math.cos(-0.19), sinRoll = Math.sin(-0.19);
      const project = ([x, y, z]: Vec) => {
        const rx = x * cosYaw + z * sinYaw;
        const rz = -x * sinYaw + z * cosYaw;
        const ry = y * cosTilt - rz * sinTilt;
        const depth = y * sinTilt + rz * cosTilt;
        const px = rx * cosRoll - ry * sinRoll;
        const py = rx * sinRoll + ry * cosRoll;
        const perspective = 4.7 / (4.7 - depth);
        return { x: centerX + px * size * perspective, y: centerY + py * size * perspective, z: depth };
      };
      const projected = vertices.map(project);

      const projectedOrbits = orbits.map(orbit => orbit.map(project));
      const projectedTicks = orbitTicks.map(tick => ({ ...tick, start: project(tick.start), end: project(tick.end) }));
      const drawOrbits = (front: boolean) => {
        ctx.lineWidth = 0.65;
        for (const [index, orbit] of projectedOrbits.entries()) {
          ctx.strokeStyle = index === 0 ? 'rgba(171,185,192,0.18)' : 'rgba(171,185,192,0.07)';
          ctx.beginPath();
          for (let i = 0; i < orbit.length; i++) {
            const start = orbit[i];
            const end = orbit[(i + 1) % orbit.length];
            if ((start.z + end.z >= 0) !== front || !overlaps([start, end], drawableBounds, 2)) continue;
            ctx.moveTo(start.x, start.y);
            ctx.lineTo(end.x, end.y);
          }
          ctx.stroke();
        }
        for (const { start, end, major } of projectedTicks) {
          if ((start.z + end.z >= 0) !== front || !overlaps([start, end], drawableBounds, 2)) continue;
          ctx.strokeStyle = `rgba(192,204,210,${major ? 0.26 : 0.12})`;
          ctx.beginPath();
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
          ctx.stroke();
        }
      };
      // Rear arcs sit behind the translucent facets; near arcs pass in front.
      // Both use the exact same yaw, tilt and perspective as the crystal.
      drawOrbits(false);

      const sorted = faces.map((face, index) => ({ face, index, z: face.reduce((sum, v) => sum + projected[v].z, 0) / 3 })).sort((a, b) => a.z - b.z);
      for (const { face, index, z } of sorted) {
        const points = face.map(v => projected[v]);
        if (!overlaps(points, drawableBounds)) continue;
        const gradient = ctx.createLinearGradient(points[0].x, points[0].y, points[2].x + 40, points[2].y + 70);
        const light = Math.max(0, z + 0.65);
        gradient.addColorStop(0, `rgba(171,185,195,${0.035 + light * 0.13 + (index % 3 === 0 ? 0.09 : 0)})`);
        gradient.addColorStop(1, `rgba(39,46,52,${0.14 + light * 0.12})`);
        ctx.beginPath();
        points.forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
        const frontness = Math.max(0, Math.min(1, (z + 0.16) / 0.32));
        const softFront = frontness * frontness * (3 - 2 * frontness);
        ctx.lineWidth = 0.5 + softFront * 0.35;
        ctx.strokeStyle = `rgba(209,222,230,${0.10 + light * 0.33})`;
        ctx.shadowColor = 'rgba(213,233,243,0.40)';
        ctx.shadowBlur = softFront * 5;
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      drawOrbits(true);
      for (const [i, p] of projected.entries()) {
        const visibility = i === 0 || i === 13 ? 1 : Math.max(0, Math.min(1, (p.z + 0.22) / 0.3));
        if (visibility === 0 || !overlaps([p], drawableBounds)) continue;
        ctx.globalAlpha = visibility * visibility * (3 - 2 * visibility);
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, 12);
        glow.addColorStop(0, 'rgba(231,241,246,0.5)');
        glow.addColorStop(0.2, 'rgba(214,229,237,0.15)');
        glow.addColorStop(1, 'rgba(214,229,237,0)');
        ctx.fillStyle = glow;
        ctx.fillRect(p.x - 12, p.y - 12, 24, 24);
        ctx.fillStyle = 'rgba(237,245,248,0.8)';
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.35, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    const tick = (now: number) => {
      frame = 0;
      if (!visible || document.hidden || !hasArea) return;
      const dt = previous ? Math.min((now - previous) / 1000, 0.05) : 0;
      if (!reduced) {
        time += dt;
        [easedX, velocityX] = followPointer(easedX, velocityX, pointerX, dt);
        [easedY, velocityY] = followPointer(easedY, velocityY, pointerY, dt);
      }
      previous = now;
      draw();
      if (!reduced) frame = requestAnimationFrame(tick);
    };
    const resume = () => {
      cancelAnimationFrame(frame);
      previous = 0;
      if (visible && !document.hidden) frame = requestAnimationFrame(tick);
    };
    const updateVisibleBounds = () => {
      const bounds = stage.getBoundingClientRect();
      visibleBounds = {
        left: Math.max(rasterBounds.left, -bounds.left),
        top: Math.max(rasterBounds.top, -bounds.top),
        right: Math.min(rasterBounds.right, document.documentElement.clientWidth - bounds.left),
        bottom: Math.min(rasterBounds.bottom, window.innerHeight - bounds.top),
      };
    };
    const resize = () => {
      const bounds = stage.getBoundingClientRect();
      const heroBounds = pointerSurface.getBoundingClientRect();
      width = bounds.width;
      height = bounds.height;
      // Allocate pixels only for the part inside the hero/page, not the whole
      // oversized stage. Scrolling updates culling, never reallocates the buffer.
      rasterBounds = {
        left: Math.max(0, heroBounds.left - bounds.left, -bounds.left),
        top: Math.max(0, heroBounds.top - bounds.top),
        right: Math.min(width, heroBounds.right - bounds.left, document.documentElement.clientWidth - bounds.left),
        bottom: Math.min(height, heroBounds.bottom - bounds.top),
      };
      const cropWidth = Math.max(0, rasterBounds.right - rasterBounds.left);
      const cropHeight = Math.max(0, rasterBounds.bottom - rasterBounds.top);
      hasArea = cropWidth > 0 && cropHeight > 0;
      canvas.style.left = `${rasterBounds.left}px`;
      canvas.style.top = `${rasterBounds.top}px`;
      canvas.style.width = `${cropWidth}px`;
      canvas.style.height = `${cropHeight}px`;
      const ratio = Math.min(devicePixelRatio || 1, 2);
      const pixelWidth = Math.max(1, Math.round(cropWidth * ratio));
      const pixelHeight = Math.max(1, Math.round(cropHeight * ratio));
      if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
      if (canvas.height !== pixelHeight) canvas.height = pixelHeight;
      ctx.setTransform(ratio, 0, 0, ratio, -rasterBounds.left * ratio, -rasterBounds.top * ratio);
      updateVisibleBounds();
      draw();
    };
    const onPointer = (event: PointerEvent) => {
      if (reduced || event.pointerType !== 'mouse') return;
      const bounds = pointerSurface.getBoundingClientRect();
      if (!bounds.width || !bounds.height) return;
      pointerX = Math.max(-1, Math.min(1, ((event.clientX - bounds.left) / bounds.width - 0.5) * 2));
      pointerY = Math.max(-1, Math.min(1, ((event.clientY - bounds.top) / bounds.height - 0.5) * 2));
    };
    const onLeave = () => { pointerX = 0; pointerY = 0; };
    const onMotion = () => { reduced = motion.matches; resume(); };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(stage);
    resizeObserver.observe(pointerSurface);
    const observer = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; resume(); }, { threshold: 0.02 });
    observer.observe(canvas);
    pointerSurface.addEventListener('pointermove', onPointer);
    pointerSurface.addEventListener('pointerleave', onLeave);
    window.addEventListener('blur', onLeave);
    window.addEventListener('scroll', updateVisibleBounds, { passive: true });
    motion.addEventListener('change', onMotion);
    document.addEventListener('visibilitychange', resume);
    resize();
    resume();
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver.disconnect();
      observer.disconnect();
      pointerSurface.removeEventListener('pointermove', onPointer);
      pointerSurface.removeEventListener('pointerleave', onLeave);
      window.removeEventListener('blur', onLeave);
      window.removeEventListener('scroll', updateVisibleBounds);
      motion.removeEventListener('change', onMotion);
      document.removeEventListener('visibilitychange', resume);
    };
  }, []);

  const edgeFade = 'linear-gradient(90deg, transparent, #000 48px, #000 calc(100% - 48px), transparent), linear-gradient(0deg, transparent, #000 48px, #000 calc(100% - 48px), transparent)';
  return (
    <div ref={stageRef} className="faceted-stage" aria-hidden="true">
      <canvas
        ref={canvasRef}
        className="hero-crystal-canvas"
        style={{
          position: 'absolute',
          display: 'block',
          pointerEvents: 'none',
          width: 0,
          height: 0,
          WebkitMaskImage: edgeFade,
          maskImage: edgeFade,
          WebkitMaskComposite: 'source-in',
          maskComposite: 'intersect',
        }}
      />
    </div>
  );
}
